import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaInfoCircle, FaBlog, FaEnvelope, FaConciergeBell, FaHome, FaProjectDiagram, FaHandshake } from 'react-icons/fa';
import { FaInstagram, FaYoutube } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';

import { useDispatch, useSelector } from 'react-redux';
import { getAdminInfo } from '../features/adminInfo/adminInfoSlice';

import './Sidebar.css';

const sidebarVariants = {
  open: {
    x: 0,
    transition: { type: 'spring', stiffness: 300, damping: 30 },
  },
  closed: {
    x: '100%',
    transition: { type: 'spring', stiffness: 300, damping: 30, delay: 0.2 },
  },
};

const navListVariants = {
  open: {
    transition: { staggerChildren: 0.07, delayChildren: 0.2 }
  },
  closed: {
    transition: { staggerChildren: 0.05, staggerDirection: -1 }
  }
};

const navItemVariants = {
  open: { y: 0, opacity: 1, transition: { y: { stiffness: 1000, velocity: -100 } } },
  closed: { y: 50, opacity: 0, transition: { y: { stiffness: 1000 } } }
};

const socialContainerVariants = {
  open: {
    opacity: 1,
    y: 0,
    transition: { delay: 0.5, duration: 0.4 }
  },
  closed: { 
    opacity: 0, 
    y: 20 
  }
};

const Sidebar = ({ isOpen, toggle }) => {
  const dispatch = useDispatch();
  const { info } = useSelector((state) => state.adminInfo);

  useEffect(() => {
    dispatch(getAdminInfo());
  }, [dispatch]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="sidebar-backdrop"
            onClick={toggle}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            className="sidebar"
            variants={sidebarVariants}
            initial="closed"
            animate="open"
            exit="closed"
          >
            <button className="sidebar-close-button" onClick={toggle}>
              <FaTimes />
            </button>

            <div className="sidebar-header">
              <h3 className="sidebar-title">Menu</h3>
            </div>

            <motion.ul className="sidebar-nav" variants={navListVariants}>
              <motion.li variants={navItemVariants}>
                <Link to="/" onClick={toggle}>
                  <FaHome className="sidebar-icon" />
                  <span>Home</span>
                </Link>
              </motion.li>
              <motion.li variants={navItemVariants}>
                <Link to="/about" onClick={toggle}>
                  <FaInfoCircle className="sidebar-icon" />
                  <span>About Us</span>
                </Link>
              </motion.li>
              <motion.li variants={navItemVariants}>
                <Link to="/services" onClick={toggle}>
                  <FaProjectDiagram className="sidebar-icon" />
                  <span>Projects</span>
                </Link>
              </motion.li>
              <motion.li variants={navItemVariants}>
                <Link to="/blogs" onClick={toggle}>
                  <FaBlog className="sidebar-icon" />
                  <span>Blog</span>
                </Link>
              </motion.li>
              <motion.li variants={navItemVariants}>
                <Link to="/be-our-partner" onClick={toggle}>
                  <FaHandshake className="sidebar-icon" />
                  <span>Be Our Partner</span>
                </Link>
              </motion.li>
              <motion.li variants={navItemVariants}>
                <Link to="/contact" onClick={toggle}>
                  <FaEnvelope className="sidebar-icon" />
                  <span>Contact</span>
                </Link>
              </motion.li>
              <motion.li variants={navItemVariants}>
                <Link to="https://drive.google.com/drive/folders/1mTQcwV2uaELJsYLX4Q3S-42s5DNEOt8E" onClick={toggle}>
                  <FaConciergeBell className="sidebar-icon" />
                  <span>PIF & Documents</span>
                </Link>
              </motion.li>
            </motion.ul>

            <motion.div 
              className="sidebar-footer"
              variants={socialContainerVariants}
              initial="closed"
              animate="open"
              exit="closed"
            >
              <div className="sidebar-divider"></div>
              <p className="sidebar-social-title">Follow Us</p>
              <div className="sidebar-socials">
                {info?.instagramUrl && (
                  <a href={info.instagramUrl} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                    <FaInstagram />
                  </a>
                )}
                {info?.xUrl && (
                  <a href={info.xUrl} target="_blank" rel="noopener noreferrer" aria-label="X">
                    <FaXTwitter />
                  </a>
                )}
                {info?.youtubeUrl && (
                  <a href={info.youtubeUrl} target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                    <FaYoutube />
                  </a>
                )}
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default Sidebar;